import { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";
import { useInstallPrompt, isStandalone } from "@/lib/pwa";

const DISMISS_KEY = "pop_erp_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

/**
 * Banner flutuante no rodapé (mobile) sugerindo instalar o ERP como app.
 * - Só aparece quando o browser disparou `beforeinstallprompt`.
 * - Só em telas mobile (< 768px), pois em desktop o Chrome já mostra o
 *   ícone de instalar na barra de endereços.
 * - Dispensável (X); a dispensa dura 7 dias no localStorage.
 * - Não aparece quando o app já está instalado (display standalone).
 */
export default function InstallAppBanner() {
  const { canInstall, install } = useInstallPrompt();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!canInstall) { setVisible(false); return; }
    if (isStandalone()) { setVisible(false); return; }
    // Só em mobile
    if (typeof window !== "undefined" && window.innerWidth >= 768) { setVisible(false); return; }
    // Verifica dispensa recente
    try {
      const dismissed = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (dismissed && Date.now() - dismissed < DISMISS_TTL_MS) {
        setVisible(false);
        return;
      }
    } catch (err) { console.warn("InstallAppBanner dismiss check:", err); }
    setVisible(true);
  }, [canInstall]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (err) { console.warn("InstallAppBanner dismiss save:", err); }
    setVisible(false);
  };

  const handleInstall = async () => {
    const accepted = await install();
    if (accepted) setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 z-[55] bg-black text-white border border-yellow-500 shadow-2xl md:hidden"
      role="dialog"
      aria-label="Instalar o POP-ERP como aplicativo"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-center gap-3 px-3 py-3">
        <div className="w-10 h-10 shrink-0 bg-white flex items-center justify-center">
          <img src="/icon-192.png" alt="" className="w-9 h-9 object-contain" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold">Instalar App</div>
          <div className="text-xs leading-snug">
            Instale o <strong>POP-ERP</strong> no seu celular e acesse direto da tela inicial.
          </div>
        </div>
        <button
          className="flex items-center gap-1 bg-yellow-500 text-black font-bold text-xs px-3 py-2"
          onClick={handleInstall}
          data-testid="pwa-install-btn"
        >
          <DownloadSimple size={14} weight="bold" /> Instalar
        </button>
        <button
          className="p-1 opacity-60 hover:opacity-100"
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label="Fechar"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
